<template>
  <div v-if="isVisible" class="modal-backdrop">
    <div class="modal-content">
      <p>{{ message }}</p>

      <p v-if="showRuleSelector"><strong>{{ ruleLabel }}</strong></p>
      <select
        v-if="showRuleSelector && ruleOptions.length"
        v-model="selectedRule"
        class="modal-select"
      >
        <option value="" disabled>{{ rulePlaceholder }}</option>
        <option v-for="rule in ruleOptions" :key="rule.id" :value="rule.id">
          {{ rule.name }}
        </option>
      </select>

      <p v-if="showNameInput" style="margin-top: 10px"><strong>{{ nameLabel }}</strong></p>
      <input
        v-if="showNameInput"
        v-model="customName"
        type="text"
        class="modal-input"
        :placeholder="namePlaceholder"
      >

      <p v-if="showJudgeSelect" style="margin-top: 10px"><strong>{{ judgeLabel }}</strong></p>
      <div v-if="showJudgeSelect" class="modal-multiselect">
        <label v-for="judge in judgeOptions" :key="judge.id" class="checkbox-row">
          <input
            type="checkbox"
            :value="judge.id"
            v-model="selectedJudgeIds"
          />
          <span>{{ judgeLabelText(judge) }}</span>
        </label>
        <p v-if="!judgeOptions.length" class="muted">{{ judgePlaceholder }}</p>
      </div>

      <template v-if="showVideoSourceSwitch">
        <p style="margin-top: 10px"><strong>{{ videoSourceLabel }}</strong></p>
        <div class="source-switch">
          <button
            type="button"
            class="source-switch-btn"
            :class="{ 'source-switch-btn--active': selectedVideoSource === 'local' }"
            @click="selectedVideoSource = 'local'"
          >
            {{ localSourceLabel }}
          </button>
          <button
            type="button"
            class="source-switch-btn"
            :class="{ 'source-switch-btn--active': selectedVideoSource === 'external' }"
            @click="selectedVideoSource = 'external'"
          >
            {{ externalSourceLabel }}
          </button>
        </div>
      </template>

      <p v-if="shouldShowVideoInput" style="margin-top: 10px"><strong>{{ videoLabel }}</strong></p>
      <input
        v-if="shouldShowVideoInput"
        v-model="videoLink"
        type="text"
        class="modal-input"
        :placeholder="videoPlaceholder"
        required
      >

      <template v-if="shouldShowExternalAthleteSelect">
        <p style="margin-top: 10px"><strong>{{ externalAthleteLabel }}</strong></p>
        <div class="external-athlete-search-wrap">
          <input
            v-model.trim="externalAthleteSearch"
            type="text"
            class="modal-input"
            :placeholder="externalAthleteSearchPlaceholder"
            :disabled="externalAthletesLoading"
            @input="handleExternalAthleteSearchInput"
            @focus="isExternalAthleteSearchFocused = true"
            @blur="handleExternalAthleteSearchBlur"
          >
          <div
            v-if="showExternalAthleteResults"
            class="external-athlete-results"
          >
            <button
              v-for="athlete in filteredExternalAthleteOptions"
              :key="athlete.value"
              type="button"
              class="external-athlete-option"
              :class="{ 'external-athlete-option--active': athlete.value === selectedExternalAthleteId }"
              @mousedown.prevent="selectExternalAthlete(athlete)"
            >
              {{ athlete.label }}
            </button>
          </div>
        </div>
        <div v-if="selectedExternalAthlete" class="selected-athlete-card">
          <span>{{ selectedExternalAthlete.label }}</span>
          <button type="button" class="selected-athlete-clear" @click="clearSelectedExternalAthlete">
            {{ clearSelectionLabel }}
          </button>
        </div>
        <p v-if="externalAthletesLoading" class="muted mt-2 mb-0">{{ externalAthleteLoadingLabel }}</p>
        <p v-else-if="!externalAthleteSearch" class="muted mt-2 mb-0">{{ externalAthletePlaceholder }}</p>
        <p v-else-if="externalAthleteSearch && !filteredExternalAthleteOptions.length" class="muted mt-2 mb-0">
          {{ externalAthleteNoResultsLabel }}
        </p>
      </template>

      <p v-if="showUserIdInput" style="margin-top: 10px"><strong>User ID</strong></p>
      <input
        v-if="showUserIdInput"
        v-model.trim="manualUserId"
        type="number"
        min="1"
        class="modal-input"
        placeholder="Ej: 123"
      >

      <template v-if="shouldShowRoundSourceSelect">
        <p style="margin-top: 10px"><strong>{{ competitionLabel }}</strong></p>
        <select v-model="selectedCompetitionId" class="modal-select">
          <option value="" disabled>{{ competitionPlaceholder }}</option>
          <option v-for="competition in competitionOptions" :key="competition.id" :value="competition.id">
            {{ competition.label }}
          </option>
        </select>

        <p style="margin-top: 10px"><strong>{{ stageLabel }}</strong></p>
        <select v-model="selectedStageNumber" class="modal-select" :disabled="!selectedCompetitionId">
          <option value="" disabled>{{ stagePlaceholder }}</option>
          <option v-for="stage in stageOptions" :key="stage.value" :value="stage.value">
            {{ stage.label }}
          </option>
        </select>

        <p style="margin-top: 10px"><strong>{{ athleteLabel }}</strong></p>
        <select v-model="selectedRoundItemId" class="modal-select" :disabled="!selectedCompetitionId || !selectedStageNumber">
          <option value="" disabled>{{ athletePlaceholder }}</option>
          <option v-for="athlete in athleteOptions" :key="athlete.value" :value="athlete.value">
            {{ athlete.label }}
          </option>
        </select>
      </template>

      <div class="modal-buttons">
        <button
          @click="confirmAction"
          class="btn btn-primary"
          :disabled="!isValid"
        >
          {{ $t('competitions.btn_confirm') }}
        </button>

        <button v-if="showCancel" @click="cancelAction" class="btn btn-secondary">{{ $t('competitions.btn_cancel') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ConfirmationModal",
  props: {
    message: { type: String, required: true },
    showCancel: { type: Boolean, default: true },
    showRuleSelector: { type: Boolean, default: false },
    ruleOptions: { type: Array, default: () => [] },
    showNameInput: { type: Boolean, default: false },
    initialRule: { type: [String, Number], default: '' },
    initialName: { type: String, default: '' },
    showJudgeSelect: { type: Boolean, default: false },
    judgeOptions: { type: Array, default: () => [] },
    showVideoInput: { type: Boolean, default: false },
    showUserIdInput: { type: Boolean, default: false },
    showRoundSourceSelect: { type: Boolean, default: false },
    showVideoSourceSwitch: { type: Boolean, default: false },
    showExternalAthleteSelect: { type: Boolean, default: false },
    externalAthleteOptions: { type: Array, default: () => [] },
    externalAthletesLoading: { type: Boolean, default: false },
    competitionRounds: { type: Array, default: () => [] },
  },
  data() {
    return {
      isVisible: false,
      selectedRule: '',
      customName: '',
      selectedJudgeIds: [],
      videoLink: '',
      manualUserId: '',
      selectedCompetitionId: '',
      selectedStageNumber: '',
      selectedRoundItemId: '',
      selectedExternalAthleteId: '',
      externalAthleteSearch: '',
      isExternalAthleteSearchFocused: false,
      selectedVideoSource: 'local',
    };
  },
  computed: {
    isValid() {
      if (this.showRuleSelector && !this.selectedRule) return false;
      if (this.showJudgeSelect && !this.selectedJudgeIds.length) return false;
      if (this.shouldShowVideoInput && !(this.videoLink || '').trim()) return false;
      if (this.shouldShowRoundSourceSelect && !this.selectedRoundItemId) return false;
      return true;
    },
    shouldShowVideoInput() {
      if (!this.showVideoInput) return false;
      if (!this.showVideoSourceSwitch) return true;
      return this.selectedVideoSource === 'external';
    },
    shouldShowRoundSourceSelect() {
      if (!this.showRoundSourceSelect) return false;
      if (!this.showVideoSourceSwitch) return true;
      return this.selectedVideoSource === 'local';
    },
    shouldShowExternalAthleteSelect() {
      if (!this.showExternalAthleteSelect) return false;
      if (!this.showVideoSourceSwitch) return false;
      return this.selectedVideoSource === 'external';
    },
    sortedExternalAthleteOptions() {
      return [...(this.externalAthleteOptions || [])].sort((a, b) => {
        const labelA = String(a?.label || '').toLocaleLowerCase();
        const labelB = String(b?.label || '').toLocaleLowerCase();
        return labelA.localeCompare(labelB);
      });
    },
    filteredExternalAthleteOptions() {
      const normalizedSearch = this.normalizeSearchText(this.externalAthleteSearch);
      if (!normalizedSearch) return [];

      const searchTerms = normalizedSearch.split(' ').filter(Boolean);

      return this.sortedExternalAthleteOptions.filter((athlete) => {
        const normalizedLabel = this.normalizeSearchText(athlete?.searchText || athlete?.label || '');
        if (!normalizedLabel) return false;

        return searchTerms.every(term => normalizedLabel.includes(term));
      }).slice(0, 12);
    },
    selectedExternalAthlete() {
      if (!this.selectedExternalAthleteId) return null;
      return this.sortedExternalAthleteOptions.find(
        athlete => String(athlete.value) === String(this.selectedExternalAthleteId)
      ) || null;
    },
    showExternalAthleteResults() {
      return this.isExternalAthleteSearchFocused
        && !!this.externalAthleteSearch
        && !!this.filteredExternalAthleteOptions.length;
    },
    ruleLabel() {
      return this.$t('training.modal.rule_label');
    },
    rulePlaceholder() {
      return this.$t('training.modal.rule_placeholder');
    },
    nameLabel() {
      return this.$t('training.modal.name_label');
    },
    namePlaceholder() {
      return this.$t('training.modal.name_placeholder');
    },
    judgeLabel() {
      return this.$t('training.supervisor.modal.judge_label');
    },
    judgePlaceholder() {
      return this.$t('training.supervisor.modal.judge_placeholder');
    },
    videoLabel() {
      return this.$t('training.supervisor.modal.video_label');
    },
    videoPlaceholder() {
      return this.$t('training.supervisor.modal.video_placeholder');
    },
    videoSourceLabel() {
      return this.$t('training.supervisor.modal.video_source_label') || 'Origen del vídeo';
    },
    localSourceLabel() {
      return this.$t('training.supervisor.modal.video_source_local') || 'Local';
    },
    externalSourceLabel() {
      return this.$t('training.supervisor.modal.video_source_external') || 'Externa';
    },
    externalAthleteLabel() {
      return this.$t('training.supervisor.modal.external_athlete_label') || 'Atleta (opcional)';
    },
    externalAthletePlaceholder() {
      return this.$t('training.supervisor.modal.external_athlete_placeholder') || 'Sin atleta / fallback backend';
    },
    externalAthleteSearchPlaceholder() {
      return this.$t('training.supervisor.modal.external_athlete_search_placeholder') || 'Escribe nombre o apellido';
    },
    externalAthleteLoadingLabel() {
      return this.$t('training.loading') || 'Cargando';
    },
    externalAthleteNoResultsLabel() {
      return this.$t('training.supervisor.modal.external_athlete_no_results') || 'No hay atletas que coincidan con la búsqueda';
    },
    clearSelectionLabel() {
      return this.$t('training.supervisor.modal.external_athlete_clear') || 'Quitar';
    },
    competitionLabel() {
      return this.$t('training.supervisor.modal.competition_label') || 'Competición';
    },
    competitionPlaceholder() {
      return this.$t('training.supervisor.modal.competition_placeholder') || 'Selecciona una competición';
    },
    stageLabel() {
      return this.$t('training.supervisor.modal.stage_label') || 'Etapa';
    },
    stagePlaceholder() {
      return this.$t('training.supervisor.modal.stage_placeholder') || 'Selecciona una etapa';
    },
    athleteLabel() {
      return this.$t('training.supervisor.modal.athlete_label') || 'Atleta';
    },
    athletePlaceholder() {
      return this.$t('training.supervisor.modal.athlete_placeholder') || 'Selecciona un atleta';
    },
    competitionOptions() {
      return (this.competitionRounds || []).map(competition => ({
        id: String(competition?.competition_id || ''),
        label: competition?.competition_name || `#${competition?.competition_id || '—'}`,
      })).filter(option => option.id);
    },
    selectedCompetition() {
      const selectedId = Number(this.selectedCompetitionId);
      if (!selectedId) return null;
      return (this.competitionRounds || []).find(item => Number(item?.competition_id) === selectedId) || null;
    },
    stageOptions() {
      const rounds = this.selectedCompetition?.rounds || [];
      return rounds
        .map(round => ({
          value: String(round?.round_number ?? ''),
          label: `${this.$t('training.round_name') || 'Ronda'} ${round?.round_number ?? ''}`,
        }))
        .filter(option => option.value !== '');
    },
    athleteOptions() {
      const selectedStage = Number(this.selectedStageNumber);
      if (!this.selectedCompetition || !selectedStage) return [];

      const round = (this.selectedCompetition.rounds || []).find(item => Number(item?.round_number) === selectedStage);
      const items = round?.items || [];

      return items.map(item => {
        const id = String(item?.id || '');
        const fullName = [item?.athlete_name, item?.athlete_surname].filter(Boolean).join(' ').trim();
        return {
          value: id,
          label: fullName ? `${fullName} (#${id})` : `#${id}`,
          raw: item,
        };
      }).filter(option => option.value);
    },
    selectedAthleteRound() {
      const selectedId = Number(this.selectedRoundItemId);
      if (!selectedId) return null;
      const selected = this.athleteOptions.find(item => Number(item.value) === selectedId);
      return selected?.raw || null;
    }
  },
  watch: {
    selectedCompetitionId() {
      this.selectedStageNumber = '';
      this.selectedRoundItemId = '';
    },
    selectedStageNumber() {
      this.selectedRoundItemId = '';
    },
    selectedVideoSource(nextValue) {
      if (nextValue !== 'external') {
        this.selectedExternalAthleteId = '';
        this.externalAthleteSearch = '';
      }
    },
  },
  methods: {
    normalizeSearchText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
    },
    openModal() {
      this.selectedRule = this.initialRule || '';
      this.customName = this.initialName || '';
      this.selectedJudgeIds = [];
      this.videoLink = '';
      this.manualUserId = '';
      this.selectedCompetitionId = '';
      this.selectedStageNumber = '';
      this.selectedRoundItemId = '';
      this.selectedExternalAthleteId = '';
      this.externalAthleteSearch = '';
      this.isExternalAthleteSearchFocused = false;
      this.selectedVideoSource = 'local';
      this.isVisible = true;
    },
    handleExternalAthleteSearchInput() {
      if (!this.externalAthleteSearch) {
        this.selectedExternalAthleteId = '';
        return;
      }

      const selectedLabel = this.selectedExternalAthlete?.label || '';
      if (this.normalizeSearchText(selectedLabel) !== this.normalizeSearchText(this.externalAthleteSearch)) {
        this.selectedExternalAthleteId = '';
      }
    },
    selectExternalAthlete(athlete) {
      this.selectedExternalAthleteId = athlete?.value || '';
      this.externalAthleteSearch = athlete?.label || '';
      this.isExternalAthleteSearchFocused = false;
    },
    clearSelectedExternalAthlete() {
      this.selectedExternalAthleteId = '';
      this.externalAthleteSearch = '';
      this.isExternalAthleteSearchFocused = false;
    },
    handleExternalAthleteSearchBlur() {
      window.setTimeout(() => {
        this.isExternalAthleteSearchFocused = false;
      }, 120);
    },
    closeModal() {
      this.isVisible = false;
    },
    confirmAction() {
      this.$emit('confirmed', {
        selectedRule: this.selectedRule,
        customName: this.customName,
        judgeIds: this.selectedJudgeIds,
        videoSource: this.selectedVideoSource,
        videoLink: this.videoLink,
        userId: this.selectedVideoSource === 'external'
          ? (this.selectedExternalAthleteId || null)
          : (this.manualUserId || null),
        selectedRoundItem: this.selectedAthleteRound,
      });
      this.closeModal();
    },
    cancelAction() {
      this.$emit('canceled');
      this.closeModal();
    },
    judgeLabelText(judge) {
      const name = [judge?.name, judge?.surname].filter(Boolean).join(' ').trim();
      if (judge?.id) {
        return `${name} (${judge.id})`;
      }
      return name;
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  z-index: 2000;
}
.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  width: min(720px, 100%);
  max-width: 720px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
}
.modal-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-top: 15px;
}
.source-switch {
  display: flex;
  gap: 10px;
  margin-top: 8px;
}

.source-switch-btn {
  flex: 1;
  border: 1px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
}

.source-switch-btn--active {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #fff;
}
.btn {
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
}
.btn-primary {
  background: #28a745;
  color: white;
}
.btn-secondary {
  background: #ccc;
}

.modal-select,
.modal-input {
  margin-top: 5px;
  width: 100%;
  padding: 8px;
  font-size: 14px;
  border-radius: 4px;
  border: 1px solid #ccc;
}
.modal-multiselect {
  margin-top: 8px;
  min-height: 160px;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
  text-align: left;
  flex: 0 0 auto;
}
.checkbox-row {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  padding: 4px 0;
  font-size: 14px;
  text-align: left;
}
.checkbox-row input {
  flex: 0 0 auto;
  margin-top: 2px;
}

.checkbox-row span {
  flex: 1;
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}
.muted {
  color: #6b7280;
  font-size: 13px;
}

.selected-athlete-card {
  margin-top: 8px;
  border: 1px solid #bfdbfe;
  background: #eff6ff;
  color: #1e3a8a;
  border-radius: 8px;
  padding: 8px 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
}

.selected-athlete-clear {
  border: none;
  background: transparent;
  color: #1d4ed8;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.external-athlete-search-wrap {
  position: relative;
}

.external-athlete-results {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 20;
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);
}

.external-athlete-option {
  width: 100%;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
  text-align: left;
  padding: 10px 12px;
  color: #374151;
  cursor: pointer;
}

.external-athlete-option:last-child {
  border-bottom: none;
}

.external-athlete-option:hover,
.external-athlete-option--active {
  background: #eff6ff;
}

@media (max-width: 576px) {
  .modal-backdrop {
    align-items: flex-start;
    padding: 12px;
  }
  .modal-content {
    width: 100%;
    max-height: calc(100vh - 24px);
    padding: 14px;
  }
  .modal-multiselect {
    min-height: 140px;
    max-height: 260px;
  }
  .source-switch {
    flex-direction: column;
  }
}
</style>
